import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';
import {
  getReflectionOpener,
  sendReflectionMessage,
  logReflection,
  type ReflectionChatMessage,
} from '@/services/activities';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface Props {
  onClose: () => void;
  onComplete?: (result: { chronicleEntry?: string }) => void;
}

export function ReflectionPage({ onClose, onComplete }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useTelegramBackButton(onClose);

  // Load AI opener
  useEffect(() => {
    (async () => {
      try {
        const opener = await getReflectionOpener();
        setMessages([{ id: '1', role: 'assistant', content: opener }]);
      } catch {
        setMessages([{
          id: '1',
          role: 'assistant',
          content: 'Привет! Давай вспомним, как прошёл твой день. Что было самым важным сегодня?',
        }]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userText = input.trim();
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Build history for API
    const history: ReflectionChatMessage[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const result = await sendReflectionMessage(userText, history);

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: result.reply,
      };
      setMessages((prev) => [...prev, aiResponse]);

      if (result.isComplete) {
        setIsComplete(true);
      }
    } catch {
      const fallback: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Спасибо за ответ. Расскажи ещё — что запомнилось больше всего?',
      };
      setMessages((prev) => [...prev, fallback]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleComplete = async () => {
    try {
      const result = await logReflection('daily', messages.map(m => m.content).join('\n'));
      onComplete?.({ chronicleEntry: result.chronicleEntry?.narrative });
    } catch (error) {
      console.error('Failed to log reflection:', error);
      onComplete?.({});
    }
    onClose();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-3 border-primary-700 border-t-primary-400"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-center p-4 border-b border-primary-800/50">
        <h1 className="text-white font-medium">Вечерняя рефлексия</h1>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        <AnimatePresence mode="popLayout">
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-primary-800/50 text-primary-200 rounded-bl-sm border border-primary-700/30'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-primary-800/50 rounded-2xl rounded-bl-sm px-4 py-3 border border-primary-700/30">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Input or Complete button */}
      <div className="p-4 border-t border-primary-800/50">
        {isComplete ? (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleComplete}
            className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium"
          >
            В город
          </motion.button>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Напишите..."
              disabled={isTyping}
              className="flex-1 bg-primary-800/50 border border-primary-700/50 rounded-xl px-4 py-3 text-white text-sm placeholder-primary-600 focus:outline-none focus:border-indigo-500/50 disabled:opacity-50"
            />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              className="px-4 rounded-xl bg-indigo-600 text-white disabled:opacity-50"
            >
              →
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
}
